import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import useWhatsAppWebAcesso from '../../hooks/useWhatsAppWebAcesso';
import {
  LayoutDashboard, Users, Building2, Kanban, Globe, Settings, Home,
  ShieldCheck, UserSquare, FileText, ShoppingBag, ChevronDown,
  Link2, UserCheck, Building, ScrollText, SendHorizonal, Smartphone, Megaphone,
  MessageCircle, CalendarClock, Flame, Ban, Shield, PhoneCall,
  ChevronLeft, ChevronRight as ChevronRightIcon,
  HardHat, ClipboardList, TrendingUp, Bot, CalendarCheck, Radio,
  Package, Wrench, QrCode, AlertTriangle, Mail, LogOut, LogIn,
  Layers, TrendingDown, Target, ShoppingCart, ArrowLeftRight, ClipboardCheck, HeartHandshake, Bookmark, Receipt, ImagePlus
} from 'lucide-react';

const COMUNICACOES_NAV = [
  { to: '/whatsapp', icon: Smartphone, label: 'WhatsApp (Evolution)' },
  { to: '/whatsapp-oficial', icon: Shield, label: 'API Oficial Meta' },
  { to: '/envios-massa', icon: SendHorizonal, label: 'Envios em Massa' },
  { to: '/aquecimento', icon: Flame, label: 'Aquecimento de Leads' },
  { to: '/agendamentos', icon: CalendarClock, label: 'Agendamentos' },
  { to: '/comunicacoes/sdr-voz', icon: PhoneCall, label: 'SDR por Voz' },
  { to: '/automacoes', icon: Bot, label: 'Automações (Iago)' },
  { to: '/marketing/chatwoot', icon: MessageCircle, label: 'Chatwoot' },
];

const VENDAS_NAV = [
  { to: '/vendas/clientes', icon: Users, label: 'Clientes' },
  { to: '/vendas/pre-reservas', icon: Bookmark, label: 'Pré-Reservas' },
  { to: '/vendas/propostas', icon: FileText, label: 'Propostas' },
  { to: '/vendas/contratos', icon: ScrollText, label: 'Contratos' },
  { to: '/vendas/boletos', icon: Receipt, label: 'Boletos' },
];

const PARCERIAS_NAV = [
  { to: '/vendas/corretores', icon: UserCheck, label: 'Corretores' },
  { to: '/vendas/parceiros', icon: HeartHandshake, label: 'Parceiros' },
  { to: '/vendas/funil-corretores', icon: Kanban, label: 'Funil de Leads Corretores' },
  { to: '/vendas/corretores-reaquecidos', icon: Flame, label: 'Corretores Reaquecidos' },
  { to: '/vendas/elo', icon: Link2, label: 'Elo' },
  { to: '/vendas/imobiliarias', icon: Building, label: 'Imobiliárias' },
  { to: '/vendas/eventos', icon: CalendarCheck, label: 'Eventos' },
];

const PORTAL_CLIENTE_NAV = [
  { to: '/portal-clientes', icon: UserSquare, label: 'Usuários' },
  { to: '/portal-clientes/evolucao', icon: HardHat, label: 'Evolução das Obras' },
  { to: '/portal-clientes/chamados', icon: ClipboardList, label: 'Chamados' },
  { to: '/portal-clientes/reajuste-incc', icon: TrendingUp, label: 'Reajuste INCC' },
];

const MARKETING_NAV = [
  { to: '/marketing/trafego', icon: Target, label: 'Gestor de Tráfego' },
  { to: '/marketing/imagens', icon: ImagePlus, label: 'Criar Imagens' },
  { to: '/marketing/postiz', icon: CalendarClock, label: 'Postiz' },
  { to: '/marketing/qrcode', icon: QrCode, label: 'Gerador de QR Code' },
  { to: '/marketing/email', icon: Mail, label: 'Email Marketing' },
];

function getUser() {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
}

function CollapsedNavItem({ to, icon: Icon, label }) {
  return (
    <NavLink
      to={to}
      title={label}
      className={({ isActive }) =>
        `flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-colors ${
          isActive ? 'bg-secondary text-white' : 'text-blue-200 hover:bg-blue-800'
        }`
      }
    >
      <Icon size={18} />
    </NavLink>
  );
}

function NavGroup({ icon: Icon, label, open, onToggle, children }) {
  return (
    <div className="pt-1">
      <button
        onClick={onToggle}
        className="flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm font-medium text-blue-200 hover:bg-blue-800 w-full"
      >
        <Icon size={18} />
        <span className="flex-1 text-left">{label}</span>
        {open ? <ChevronDown size={14} /> : <ChevronRightIcon size={14} />}
      </button>
      {open && (
        <div className="ml-4 space-y-0.5 mt-0.5">
          {children}
        </div>
      )}
    </div>
  );
}

// Subgrupo aninhado dentro de um NavGroup (estilo menor, igual ao LeadsGroup)
function SubNavGroup({ icon: Icon, label, open, onToggle, children }) {
  return (
    <div className="pt-0.5">
      <button
        onClick={onToggle}
        className="flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-xs font-medium text-blue-300 hover:bg-blue-800 w-full"
      >
        <Icon size={15} />
        <span className="flex-1 text-left">{label}</span>
        {open ? <ChevronDown size={12} /> : <ChevronRightIcon size={12} />}
      </button>
      {open && (
        <div className="ml-4 space-y-0.5 mt-0.5">
          {children}
        </div>
      )}
    </div>
  );
}

function SubNavLink({ to, icon: Icon, label }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-xs font-medium ${
          isActive ? 'bg-secondary text-white' : 'text-blue-300 hover:bg-blue-800'
        }`
      }
    >
      <Icon size={15} />
      {label}
    </NavLink>
  );
}

// Submenu CRM > Leads (com botões de atalho no topo)
function LeadsGroup({ open, onToggle }) {
  return (
    <div className="pt-0.5">
      <button
        onClick={onToggle}
        className="flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-xs font-medium text-blue-300 hover:bg-blue-800 w-full"
      >
        <Users size={15} />
        <span className="flex-1 text-left">Leads</span>
        {open ? <ChevronDown size={12} /> : <ChevronRightIcon size={12} />}
      </button>
      {open && (
        <div className="ml-4 space-y-0.5 mt-0.5">
          <NavLink
            to="/leads"
            end
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-xs font-medium ${
                isActive ? 'bg-secondary text-white' : 'text-blue-300 hover:bg-blue-800'
              }`
            }
          >
            <Users size={14} /> Todos os Leads
          </NavLink>
          <NavLink
            to="/leads/kanban"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-xs font-medium ${
                isActive ? 'bg-secondary text-white' : 'text-blue-300 hover:bg-blue-800'
              }`
            }
          >
            <Kanban size={14} /> Funil de Leads
          </NavLink>
          <NavLink
            to="/leads-reaquecidos"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-xs font-medium ${
                isActive ? 'bg-secondary text-white' : 'text-blue-300 hover:bg-blue-800'
              }`
            }
          >
            <Flame size={14} /> Leads Reaquecidos
          </NavLink>
          <NavLink
            to="/blacklist"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-xs font-medium ${
                isActive ? 'bg-secondary text-white' : 'text-blue-300 hover:bg-blue-800'
              }`
            }
          >
            <Ban size={14} /> Blacklist
          </NavLink>
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ open, onToggle }) {
  const user = getUser();
  const isAdmin = user?.perfil === 'admin';
  const modulosPermitidos = user?.modulos_permitidos || null; // null = sem restrição
  const hasModulo = (key) => isAdmin || !modulosPermitidos || modulosPermitidos.includes(key);
  const hasEstoque = isAdmin || !modulosPermitidos || modulosPermitidos.includes('estoque') || modulosPermitidos.some(m => m.startsWith('estoque.'));
  const { permitido: verWhatsAppWeb } = useWhatsAppWebAcesso();
  const hasEstoqueSub = (sub) => isAdmin || !modulosPermitidos || modulosPermitidos.includes('estoque') || modulosPermitidos.includes(`estoque.${sub}`);

  // Todos os submenus começam fechados
  const [crmOpen, setCrmOpen] = useState(false);
  const [leadsOpen, setLeadsOpen] = useState(false);
  const [agendaOpen, setAgendaOpen] = useState(false);
  const [vendasOpen, setVendasOpen] = useState(false);
  const [parceriasOpen, setParceriasOpen] = useState(false);
  const [marketingOpen, setMarketingOpen] = useState(false);
  const [comunicacoesOpen, setComunicacoesOpen] = useState(false);
  const [portalClienteOpen, setPortalClienteOpen] = useState(false);
  const [estoqueOpen, setEstoqueOpen] = useState(false);
  const [ferramentasOpen, setFerramentasOpen] = useState(false);
  const [insumosOpen, setInsumosOpen] = useState(false);

  const flatItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/leads', icon: Building2, label: 'CRM' },
    { to: '/agenda', icon: CalendarCheck, label: 'Agenda' },
    { to: '/automacoes', icon: Bot, label: 'Comunicações' },
    { to: '/vendas/clientes', icon: ShoppingBag, label: 'Vendas' },
    { to: '/portal-clientes', icon: UserSquare, label: 'Portal do Cliente' },
    { to: '/vendas/corretores', icon: HeartHandshake, label: 'Parcerias' },
    { to: '/marketing/postiz', icon: Megaphone, label: 'Marketing' },
    ...(hasEstoque ? [{ to: hasEstoqueSub('dashboard') ? '/estoque' : '/estoque/itens', icon: Package, label: 'Estoque' }] : []),
    ...(isAdmin ? [{ to: '/usuarios', icon: ShieldCheck, label: 'Usuários' }] : []),
    { to: '/portais', icon: Globe, label: 'Portais' },
    { to: '/configuracoes/integracoes', icon: Settings, label: 'Configurações' },
  ];

  return (
    <div
      className={`bg-primary text-white flex flex-col shadow-lg transition-all duration-300 ease-in-out flex-shrink-0 relative z-30 ${
        open ? 'w-64' : 'w-14'
      }`}
    >
      {/* Cabeçalho / Logo */}
      <div
        className={`border-b border-blue-800 flex items-center flex-shrink-0 ${
          open ? 'p-6 justify-between' : 'p-2 justify-center'
        }`}
      >
        {open ? (
          <>
            <div className="flex flex-col items-start gap-1">
              <img
                src="/logo-inbase.svg"
                alt="Inbase Construtora"
                className="h-10 w-auto"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
              <p className="text-xs text-blue-300">CRM</p>
            </div>
            <button
              onClick={onToggle}
              title="Recolher menu"
              className="text-blue-300 hover:text-white transition-colors p-1 rounded"
            >
              <ChevronLeft size={18} />
            </button>
          </>
        ) : (
          <button
            onClick={onToggle}
            title="Expandir menu"
            className="text-blue-300 hover:text-white transition-colors p-1 rounded"
          >
            <ChevronRightIcon size={18} />
          </button>
        )}
      </div>

      {/* Navegação */}
      <nav className={`flex-1 overflow-y-auto overflow-x-hidden ${open ? 'p-3 space-y-0.5' : 'py-3 space-y-1'}`}>
        {open ? (
          <>
            {/* Dashboard */}
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm font-medium ${
                  isActive ? 'bg-secondary text-white' : 'text-blue-200 hover:bg-blue-800'
                }`
              }
            >
              <LayoutDashboard size={18} /> Dashboard
            </NavLink>

            {/* CRM */}
            <NavGroup icon={Building2} label="CRM" open={crmOpen} onToggle={() => setCrmOpen(v => !v)}>
              <LeadsGroup open={leadsOpen} onToggle={() => setLeadsOpen(v => !v)} />
              <SubNavLink to="/empreendimentos" icon={Building2} label="Empreendimentos" />
              <SubNavLink to="/imoveis" icon={Home} label="Imóveis" />
              <SubNavLink to="/pipeline" icon={Kanban} label="Pipeline" />
            </NavGroup>

            {/* Agenda */}
            <NavGroup icon={CalendarCheck} label="Agenda" open={agendaOpen} onToggle={() => setAgendaOpen(v => !v)}>
              <SubNavLink to="/agenda" icon={CalendarCheck} label="Agenda de Visitas" />
            </NavGroup>

            {/* Comunicações */}
            <NavGroup icon={Radio} label="Comunicações" open={comunicacoesOpen} onToggle={() => setComunicacoesOpen(v => !v)}>
              {verWhatsAppWeb && <SubNavLink to="/comunicacoes/whatsapp-web" icon={MessageCircle} label="WhatsApp Web" />}
              {COMUNICACOES_NAV.map(item => <SubNavLink key={item.to} {...item} />)}
            </NavGroup>

            {/* Vendas */}
            <NavGroup icon={ShoppingBag} label="Vendas" open={vendasOpen} onToggle={() => setVendasOpen(v => !v)}>
              {VENDAS_NAV.map(item => <SubNavLink key={item.to} {...item} />)}
            </NavGroup>

            {/* Parcerias */}
            <NavGroup icon={HeartHandshake} label="Parcerias" open={parceriasOpen} onToggle={() => setParceriasOpen(v => !v)}>
              {PARCERIAS_NAV.map(item => <SubNavLink key={item.to} {...item} />)}
            </NavGroup>

            {/* Portal do Cliente */}
            <NavGroup icon={UserSquare} label="Portal do Cliente" open={portalClienteOpen} onToggle={() => setPortalClienteOpen(v => !v)}>
              {PORTAL_CLIENTE_NAV.map(item => <SubNavLink key={item.to} {...item} />)}
            </NavGroup>

            {/* Marketing */}
            <NavGroup icon={Megaphone} label="Marketing" open={marketingOpen} onToggle={() => setMarketingOpen(v => !v)}>
              {MARKETING_NAV.map(item => <SubNavLink key={item.to} {...item} />)}
            </NavGroup>

            {/* Estoque (Ferramentas + Insumos/Materiais) */}
            {hasEstoque && (
              <NavGroup icon={Package} label="Estoque" open={estoqueOpen} onToggle={() => setEstoqueOpen(v => !v)}>
                <SubNavGroup icon={Wrench} label="Ferramentas" open={ferramentasOpen} onToggle={() => setFerramentasOpen(v => !v)}>
                  {hasEstoqueSub('dashboard') && <SubNavLink to="/estoque" icon={Package} label="Dashboard" />}
                  {hasEstoqueSub('itens') && <SubNavLink to="/estoque/itens" icon={Wrench} label="Ferramentas" />}
                  {hasEstoqueSub('itens') && <SubNavLink to="/estoque/funcionarios" icon={Users} label="Funcionários" />}
                  <SubNavLink to="/estoque/movimentacao-diaria" icon={LogOut} label="Saída / Devolução" />
                  {hasEstoqueSub('alertas') && <SubNavLink to="/estoque/alertas" icon={AlertTriangle} label="Manutenções" />}
                  {hasEstoqueSub('gerador_qr') && <SubNavLink to="/estoque/gerador-qr" icon={QrCode} label="Gerador de Etiquetas" />}
                  <SubNavLink to="/estoque/scanner" icon={QrCode} label="Scanner QR" />
                  {hasEstoqueSub('configuracoes') && <SubNavLink to="/estoque/configuracoes" icon={Settings} label="Configurações" />}
                </SubNavGroup>
                <SubNavGroup icon={Layers} label="Insumos / Materiais" open={insumosOpen} onToggle={() => setInsumosOpen(v => !v)}>
                  <SubNavLink to="/insumos" icon={Layers} label="Dashboard" />
                  <SubNavLink to="/insumos/materiais" icon={Package} label="Catálogo" />
                  <SubNavLink to="/insumos/entrada" icon={LogIn} label="Entrada (NF)" />
                  <SubNavLink to="/insumos/saida" icon={TrendingDown} label="Saída (Scanner)" />
                  <SubNavLink to="/insumos/pedidos" icon={ShoppingCart} label="Pedidos" />
                  <SubNavLink to="/insumos/fornecedores" icon={Building2} label="Fornecedores" />
                  <SubNavLink to="/insumos/transferencias" icon={ArrowLeftRight} label="Transferências" />
                  <SubNavLink to="/insumos/inventario" icon={ClipboardCheck} label="Inventário" />
                  <SubNavLink to="/insumos/alertas" icon={AlertTriangle} label="Alertas de Estoque" />
                </SubNavGroup>
              </NavGroup>
            )}

            {/* Usuários (admin) */}
            {isAdmin && (
              <NavLink
                to="/usuarios"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm font-medium ${
                    isActive ? 'bg-secondary text-white' : 'text-blue-200 hover:bg-blue-800'
                  }`
                }
              >
                <ShieldCheck size={18} /> Usuários
              </NavLink>
            )}

            {/* Portais e Configurações — final do menu */}
            <div className="pt-2 border-t border-blue-800 mt-2">
              <NavLink
                to="/portais"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm font-medium ${
                    isActive ? 'bg-secondary text-white' : 'text-blue-200 hover:bg-blue-800'
                  }`
                }
              >
                <Globe size={18} /> Portais
              </NavLink>
              <NavLink
                to="/configuracoes/integracoes"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm font-medium ${
                    isActive ? 'bg-secondary text-white' : 'text-blue-200 hover:bg-blue-800'
                  }`
                }
              >
                <Settings size={18} /> Configurações
              </NavLink>
            </div>
          </>
        ) : (
          flatItems.map(({ to, icon: Icon, label }) => (
            <CollapsedNavItem key={to} to={to} icon={Icon} label={label} />
          ))
        )}
      </nav>

      {open && (
        <div className="p-4 border-t border-blue-800 flex-shrink-0">
          <p className="text-xs text-blue-400">v1.0.0 — Maringá/PR</p>
        </div>
      )}
    </div>
  );
}
